import { Suspense, useRef } from 'react';

import { logger } from '../../../core/Log/logger';

import { useWemap } from './hooks/useWemap';
import { WemapBoundsPadding, WemapLivemap, WemapPinpoint } from './types/Wemap';
import { WemapDrawer } from './WemapDrawer';
import { WemapLoading } from './WemapLoading';
import { WemapMap, WemapMapProps } from './WemapMap';
import style from './WemapReact.module.css';

const BOUNDS_PADDING_SIZE = 60;

const boundsPadding: Required<WemapBoundsPadding> = {
    top: BOUNDS_PADDING_SIZE,
    right: BOUNDS_PADDING_SIZE,
    bottom: BOUNDS_PADDING_SIZE,
    left: BOUNDS_PADDING_SIZE,
};

type WemapReactProps = Omit<WemapMapProps, 'boundsPadding'>;

export function WemapReact(props: WemapReactProps) {
    const {
        renderDrawer,
        isDrawerClosable = false,
        isDrawerHideable = false,
        isDrawerOpen = false,
        ...mapProps
    } = props;

    const livemapRef = useRef<WemapLivemap | null>(null);

    const { openedPinpointId, setOpenedPinpointId } = useWemap();

    function centerToPinpoint(
        pinpoint: WemapPinpoint,
        { isDrawerVisible }: { isDrawerVisible: boolean },
    ) {
        // We could do a fitBounds for regions with geometry data here,
        // but it makes zoom in/out too much at the moment, so maybe late.
        // We keep a simple centering for any pinpoint for now.
        return livemapRef.current
            ?.easeTo({
                padding: {
                    ...boundsPadding,
                    left: isDrawerVisible
                        ? boundsPadding.left + WemapDrawer.WIDTH
                        : boundsPadding.left,
                },
                center: {
                    latitude: pinpoint.latitude,
                    longitude: pinpoint.longitude,
                },
            })
            .catch((error) => {
                logger.error('Failed to center the map on the pinpoint', { pinpoint, error });
            });
    }

    function handleDrawerCollapse() {
        const openedPinpoint = mapProps.pinpoints.find((pinpoint) => {
            return pinpoint.id === openedPinpointId;
        });

        if (!openedPinpoint) {
            return;
        }
        return centerToPinpoint(openedPinpoint, { isDrawerVisible: false });
    }

    function handleDrawerExpand() {
        const openedPinpoint = mapProps.pinpoints.find((pinpoint) => {
            return pinpoint.id === openedPinpointId;
        });

        if (!openedPinpoint) {
            return;
        }
        return centerToPinpoint(openedPinpoint, { isDrawerVisible: true });
    }

    const handleDrawerClose = async () => {
        const openedPinpoint = mapProps.pinpoints.find((pinpoint) => {
            return pinpoint.id === openedPinpointId;
        });

        void livemapRef.current
            ?.closePinpoint()
            .catch((error) => {
                logger.error('Failed to close pinpoint on drawer close', { error });
            })
            .then(() => {
                if (!openedPinpoint) {
                    return;
                }
                return centerToPinpoint(openedPinpoint, { isDrawerVisible: false });
            });

        setOpenedPinpointId(null);
    };

    const handleLivemapReady = (livemap: WemapLivemap) => {
        livemapRef.current = livemap;
        mapProps.onReady?.(livemap);
    };

    return (
        <div className={style.container}>
            {isDrawerOpen && renderDrawer ? (
                <WemapDrawer
                    isClosable={isDrawerClosable}
                    isCollapsable={isDrawerHideable}
                    onClose={handleDrawerClose}
                    onCollapse={handleDrawerCollapse}
                    onExpand={handleDrawerExpand}
                >
                    {renderDrawer({ openedPinpointId })}
                </WemapDrawer>
            ) : null}

            <Suspense fallback={<WemapLoading />}>
                <WemapMap
                    {...mapProps}
                    isDrawerOpen={isDrawerOpen}
                    boundsPadding={boundsPadding}
                    onReady={handleLivemapReady}
                />
            </Suspense>
        </div>
    );
}
